import {
  Calendar,
  Car,
  CheckCircle2,
  Clock,
  Eye,
  Trash2,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Order } from "@/components/KanbanCard";
import {
  isPending,
  isCompleted } from "@/hooks/quotes/useQuotesData";

const getStageBadge = (q: Order) => {
  if (isPending(q)) {
    return {
      label: "Orçamento Pendente",
      bg: "bg-amber-500/10 border-amber-500/20 text-amber-400",
      icon: <Clock size={12} />,
    };
  }
  if (isCompleted(q)) {
    return {
      label: "Concluído e Pago",
      bg: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400",
      icon: <CheckCircle2 size={12} />,
    };
  }
  const stageNames: Record<string, string> = {
    AGUARDANDO: "Fila de Espera",
    LAVANDO: "Lavando",
    FINALIZANDO: "Secando/Finalizando",
    PRONTO: "Pronto para Entrega",
  };
  return {
    label: `Aprovado (${stageNames[q.status] || "No Pátio"})`,
    bg: "bg-blue-500/10 border-blue-500/20 text-blue-400",
    icon: <CheckCircle2 size={12} />,
  };
};

export function QuotesList({
  paginatedQuotes,
  deleteQuote,
  convertToOrder,
  setSelectedQuote,
  setPdfModalOpen,
}: {
  paginatedQuotes: Order[];
  deleteQuote: any;
  convertToOrder: any;
  setSelectedQuote: (o: Order) => void;
  setPdfModalOpen: (val: boolean) => void;
}) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {paginatedQuotes.map((quote) => {
        const badge = getStageBadge(quote);
        const pending = isPending(quote);

        return (
          <div
            key={quote.id}
            className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col justify-between hover:border-slate-700 transition-colors shadow-lg"
          >
            <div>
              <div className="flex justify-between items-start mb-4">
                <span
                  className={`px-2.5 py-1 rounded-md border text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${badge.bg}`}
                >
                  {badge.icon}
                  {badge.label}
                </span>
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Calendar size={14} />
                  {new Date(quote.created_at).toLocaleDateString("pt-BR")}
                </span>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-2 text-white font-bold text-lg">
                  <Car size={18} className="text-slate-400" />
                  <span>{quote.vehicle_model}</span>
                  <span className="text-xs px-2 py-0.5 bg-slate-800 border border-slate-700 font-mono text-blue-400 rounded">
                    {quote.plate}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-sm mt-1">
                  <User size={14} />
                  <span>{quote.customer_name}</span>
                  <span className="text-xs text-slate-500">
                    ({quote.customer_phone})
                  </span>
                </div>
              </div>

              <div className="bg-slate-950/60 rounded-xl p-3 border border-slate-800/80 mb-4 space-y-1.5">
                <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-2">
                  Serviços Orçados
                </div>
                {Array.isArray(quote.services) &&
                  quote.services.map((svc: any, i: number) => (
                    <div
                      key={i}
                      className="flex justify-between items-center text-xs"
                    >
                      <span className="text-slate-300">{svc.name}</span>
                      <span className="text-slate-400 font-medium">
                        R$ {Number(svc.price).toFixed(2)}
                      </span>
                    </div>
                  ))}
                <div className="border-t border-slate-800 pt-2 mt-2 flex justify-between items-center text-sm font-bold">
                  <span className="text-slate-400">Total:</span>
                  <span className="text-emerald-400 text-base">
                    R$ {Number(quote.total).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-800/60">
              <Button
                size="sm"
                variant="ghost"
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2"
                onClick={() => {
                  if (
                    confirm("Tem certeza que deseja excluir este orçamento?")
                  ) {
                    deleteQuote.mutate(quote.id);
                  }
                }}
              >
                <Trash2 size={16} />
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 gap-1.5 text-xs"
                  onClick={() => {
                    setSelectedQuote(quote);
                    setPdfModalOpen(true);
                  }}
                >
                  <Eye size={14} /> Ver PDF
                </Button>

                {pending && (
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-500 text-white font-bold gap-1.5 text-xs shadow-md shadow-blue-600/20"
                    onClick={() => convertToOrder.mutate(quote.id)}
                    disabled={convertToOrder.isPending}
                  >
                    <CheckCircle2 size={14} /> Aprovar OS
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
